/**
 * Vera Academy route tree, composable under any parent path.
 *
 * The Vera Shell mounts it at `/academy/*` via `academyRoutes('/academy')`.
 * Academy is Postgres-backed (BFF `/api/academy/*`) and, like Ideas, open to
 * EVERY authenticated employee: the ProtectedRoute has no role prefix
 * (PLAN 12.2 - restricted content is per-item, not per-module). Authoring
 * surfaces appear in the nav only for holders of the academy.author
 * capability; the BFF re-checks every mutation regardless.
 *
 * Know, the glossary, the Content Studio, and Learn (courses, enrollment,
 * quizzes, certificates) are all live; the nav entries appear per capability.
 */

import { lazy, Suspense, createContext, useContext } from 'react';
import { Navigate, Outlet, type RouteObject } from 'react-router-dom';
import { FluentProvider, Spinner } from '@fluentui/react-components';
import { AppShell, type NavEntry } from '@widgets/app-shell';
import { ErrorBoundary, typography, veracityLightTheme } from '@shared/ui';
import { ProtectedRoute } from '@shared/auth';
import {
  BookRegular,
  BookLetterRegular,
  HatGraduationRegular,
  PenRegular,
  DataBarHorizontalRegular,
  SearchRegular,
  ClipboardTaskListLtrRegular,
} from '@fluentui/react-icons';
import { useAcademyMe, useMyAssignmentCount } from '@academy/api/useAcademy';

const LandingPage = lazy(() => import('@academy/pages/landing').then((m) => ({ default: m.LandingPage })));
const KnowHomePage = lazy(() => import('@academy/pages/know').then((m) => ({ default: m.KnowHomePage })));
const GlossaryPage = lazy(() => import('@academy/pages/glossary').then((m) => ({ default: m.GlossaryPage })));
const AuthoringListPage = lazy(() =>
  import('@academy/pages/authoring').then((m) => ({ default: m.AuthoringListPage }))
);
const EditorPage = lazy(() => import('@academy/pages/authoring').then((m) => ({ default: m.EditorPage })));
const CoursesPage = lazy(() => import('@academy/pages/authoring').then((m) => ({ default: m.CoursesPage })));
const CourseEditorPage = lazy(() =>
  import('@academy/pages/authoring').then((m) => ({ default: m.CourseEditorPage }))
);
const CatalogPage = lazy(() => import('@academy/pages/learn').then((m) => ({ default: m.CatalogPage })));
const ReportsPage = lazy(() => import('@academy/pages/reports').then((m) => ({ default: m.ReportsPage })));
const CoursePage = lazy(() => import('@academy/pages/learn').then((m) => ({ default: m.CoursePage })));
const LessonPage = lazy(() => import('@academy/pages/learn').then((m) => ({ default: m.LessonPage })));
const QuizPage = lazy(() => import('@academy/pages/learn').then((m) => ({ default: m.QuizPage })));
const CertificatePage = lazy(() => import('@academy/pages/learn').then((m) => ({ default: m.CertificatePage })));
const MyLearningPage = lazy(() => import('@academy/pages/learn').then((m) => ({ default: m.MyLearningPage })));
const AssignPage = lazy(() => import('@academy/pages/assign').then((m) => ({ default: m.AssignPage })));

/** Morning Coral: the one approved secondary not yet claimed by a module. */
export const ACADEMY_ACCENT = '#EE6352';

/**
 * Academy runs DM Sans; the rest of Vera is still on Segoe UI.
 *
 * `fontFamilyBase` is the one token every Fluent component reads, so
 * overriding it in a nested FluentProvider moves the whole Academy subtree and
 * nothing else. The faces are declared in vera-shell/src/index.css and cost
 * nothing until a page asks for them (docs/design/vera-visual-refresh.md).
 *
 * THIS IS THE PILOT, NOT THE DESTINATION. DM Sans sets slightly wider than
 * Segoe at the same size, so the suite-wide move is a real regression risk on
 * dense tables. Scoping it here first means we find that out on five learning
 * pages instead of on every screen in Ops. To roll out: delete this provider
 * and set `fontFamilyBase` on veracityLightTheme / veracityDarkTheme instead.
 *
 * `background: transparent` because a nested FluentProvider paints its own
 * canvas by default, which would draw a white rectangle over the shell ground.
 */
const academyTheme = { ...veracityLightTheme, fontFamilyBase: typography.fontFamily };

function LazyPage({ children }: { children: React.ReactNode }) {
  return <Suspense fallback={<Spinner style={{ padding: '2rem' }} label="Loading..." />}>{children}</Suspense>;
}

const AcademyBasePathContext = createContext<string>('');

export function useAcademyBasePath(): string {
  return useContext(AcademyBasePathContext);
}

function buildNavItems(
  basePath: string,
  canAuthor: boolean,
  canViewReports: boolean,
  canAssign: boolean,
  assignedCount: number
): NavEntry[] {
  const p = (suffix: string) => `${basePath}${suffix}` || '/';
  const items: NavEntry[] = [
    { path: p(''), label: 'Search', icon: SearchRegular },
    { kind: 'section', label: 'Know' },
    { path: p('/know'), label: 'Knowledge Base', icon: BookRegular },
    { path: p('/glossary'), label: 'Glossary', icon: BookLetterRegular },
    { kind: 'section', label: 'Learn' },
    // Only appears once something is actually assigned: an empty My Learning
    // is a dead link for most people most of the time.
    ...(assignedCount > 0
      ? [
          {
            path: p('/learn/my-learning'),
            label: 'My Learning',
            icon: ClipboardTaskListLtrRegular,
            badge: String(assignedCount),
          } as NavEntry,
        ]
      : []),
    { path: p('/learn'), label: 'Courses', icon: HatGraduationRegular },
  ];
  if (canAuthor) {
    items.push({ kind: 'section', label: 'Authoring' });
    items.push({ path: p('/authoring'), label: 'Content Studio', icon: PenRegular });
    items.push({ path: p('/authoring/courses'), label: 'Course Builder', icon: HatGraduationRegular });
  }
  if (canViewReports || canAssign) {
    items.push({ kind: 'section', label: 'Manage' });
    if (canAssign) {
      items.push({ path: p('/assign'), label: 'Assign Training', icon: ClipboardTaskListLtrRegular });
    }
    if (canViewReports) {
      items.push({ path: p('/reports'), label: 'Training Report', icon: DataBarHorizontalRegular });
    }
  }
  return items;
}

export function AcademyRootLayout() {
  const basePath = useContext(AcademyBasePathContext);
  const me = useAcademyMe();
  const assignedCount = useMyAssignmentCount();
  const navItems = buildNavItems(
    basePath,
    me.data?.canAuthor ?? false,
    me.data?.canViewReports ?? false,
    me.data?.canAssign ?? false,
    assignedCount.data?.open ?? 0
  );
  return (
    <ProtectedRoute>
      <ErrorBoundary>
        <FluentProvider theme={academyTheme} style={{ background: 'transparent' }}>
          <AppShell appId="academy" headerColor={ACADEMY_ACCENT} navItems={navItems}>
            <Outlet />
          </AppShell>
        </FluentProvider>
      </ErrorBoundary>
    </ProtectedRoute>
  );
}

/**
 * Returns the Academy route tree, sidebar links prefixed for the mount path.
 * Shell: `academyRoutes('/academy')`.
 */
export function academyRoutes(basePath: string = ''): RouteObject[] {
  return [
    {
      element: (
        <AcademyBasePathContext.Provider value={basePath}>
          <AcademyRootLayout />
        </AcademyBasePathContext.Provider>
      ),
      children: [
        // The landing IS the search: one box across Know and the glossary.
        { index: true, element: <LazyPage><LandingPage /></LazyPage> },
        { path: 'know', element: <LazyPage><KnowHomePage /></LazyPage> },
        // Same master-detail page: the slug selects the article in the right
        // pane while the grouped list stays put.
        { path: 'know/:slug', element: <LazyPage><KnowHomePage /></LazyPage> },
        { path: 'glossary', element: <LazyPage><GlossaryPage /></LazyPage> },
        { path: 'learn', element: <LazyPage><CatalogPage /></LazyPage> },
        // Static segments outrank the :courseSlug / :lessonSlug params, so
        // these must not collide with a course slugged 'certificates',
        // 'my-learning' or 'assign', or a lesson slugged 'quiz'.
        { path: 'learn/my-learning', element: <LazyPage><MyLearningPage /></LazyPage> },
        { path: 'learn/certificates/:serial', element: <LazyPage><CertificatePage /></LazyPage> },
        { path: 'learn/:courseSlug', element: <LazyPage><CoursePage /></LazyPage> },
        { path: 'learn/:courseSlug/quiz', element: <LazyPage><QuizPage /></LazyPage> },
        { path: 'learn/:courseSlug/:lessonSlug', element: <LazyPage><LessonPage /></LazyPage> },
        { path: 'reports', element: <LazyPage><ReportsPage /></LazyPage> },
        { path: 'assign', element: <LazyPage><AssignPage /></LazyPage> },
        { path: 'authoring', element: <LazyPage><AuthoringListPage /></LazyPage> },
        { path: 'authoring/courses', element: <LazyPage><CoursesPage /></LazyPage> },
        { path: 'authoring/courses/:id', element: <LazyPage><CourseEditorPage /></LazyPage> },
        { path: 'authoring/:id', element: <LazyPage><EditorPage /></LazyPage> },
        { path: '*', element: <Navigate to={basePath || '/'} replace /> },
      ],
    },
  ];
}
